import { apiBaseUrl } from "./config";

/**
 * THE LIVE CHANNEL — the storefront's subscription to the POS's own order and
 * table broadcast.
 *
 * ## Which channel this is, and why it is the right one
 *
 * `:4001` fans every order/table event out from ONE function,
 * `lib/rtdbMirror.js broadcastToPos`, over the transports the POS listens on:
 *
 *   * **socket.io**, room `tenant:<id>` — the primary, and the only one that
 *     works when the RTDB `listenerEnabled` flag is off;
 *   * **Firebase RTDB**, room `/rooms/<tenant>_POS` — the offline-tolerant
 *     fallback the POS subscribes to with `onChildAdded`.
 *
 * Both are unreachable from here. socket.io needs `socket.io-client`, which is
 * not a dependency of this app; the RTDB needs the Firebase SDK, likewise
 * absent; and either would have to open a socket from :3002 to :4001 — a second
 * self-signed certificate no remote visitor has accepted, which is the exact
 * problem `/api/backend/*` exists to solve. (The `/.ws` RTDB *emulation* on
 * :4001 is not an option either: its own header records it as dead since the
 * 2026-07-30 real-Firebase migration, it answers `{}` for `rooms`, and it never
 * pushes an update.)
 *
 * So `:4001` re-serves the SAME events as Server-Sent Events on
 * `GET /web/live/:tenantId` (mongo-api `src/controllers/web/live.js`), tapped
 * from inside `broadcastToPos` itself. That makes this stream the same channel
 * a POS terminal is on — not a parallel one that could drift, and not a polling
 * loop. `EventSource` is native, so there is no dependency, and it rides the
 * same-origin proxy like every other call in `lib/api.ts`.
 *
 * ## What arrives
 *
 * The event NAMES are the POS's own, so they can be traced straight back to the
 * server:
 *
 * | event                              | payload                                   |
 * |------------------------------------|-------------------------------------------|
 * | `SOCKET_EMIT_CREATED_ORDER`        | `LiveOrder`                               |
 * | `SOCKET_EMIT_UPDATED_ORDER`        | `LiveOrder` — incl. SETTLE and CANCELLED  |
 * | `SOCKET_EMIT_DELETED_ORDER`        | `{ _id }` (the server sends a bare id)    |
 * | `SOCKET_EMIT_UPDATED_DINEIN_TABLE` | a table, with `_id` + `dinein_floor_id`   |
 *
 * An order payload here is a REDUCED projection, not the POS's whole document:
 * `/web/*` is public, so the customer's name, phone, address and payment rows
 * are not on this stream. Money is in MAJOR units like everything else on that
 * surface.
 */

export const LIVE_EVENT = {
  ORDER_CREATED: "SOCKET_EMIT_CREATED_ORDER",
  ORDER_UPDATED: "SOCKET_EMIT_UPDATED_ORDER",
  ORDER_DELETED: "SOCKET_EMIT_DELETED_ORDER",
  TABLE_UPDATED: "SOCKET_EMIT_UPDATED_DINEIN_TABLE",
} as const;

export interface LiveOrder {
  _id: string;
  tenant_id?: string | null;
  order_number?: number | string | null;
  platform?: string | null;
  status?: string | null;
  /** Stamped by `:4001` once payments cover the total — i.e. PAID ON THE POS. */
  order_settled_at?: string | null;
  is_deleted?: boolean;
  /** MAJOR units. */
  total?: number;
  table_id?: string | null;
  floor_id?: string | null;
  table_name?: string;
  num_of_guests?: number;
  order_taker_details?: {
    staff_id?: string | null;
    staff_name?: string | null;
    staff_mobile?: string | null;
  } | null;
  updatedAt?: string | null;
  createdAt?: string | null;
}

export interface LiveTable {
  _id: string;
  dinein_floor_id?: string;
  [key: string]: unknown;
}

export interface LiveHandlers {
  onOrderCreated?: (order: LiveOrder) => void;
  onOrderUpdated?: (order: LiveOrder) => void;
  onOrderDeleted?: (orderId: string) => void;
  onTableUpdated?: (table: LiveTable) => void;
  /** Fired once per successful (re)connection, so a caller can re-sync. */
  onReady?: () => void;
}

/**
 * Open the stream for one tenant. Returns the closer.
 *
 * Reconnection is `EventSource`'s own: the server sends `retry: 4000`, and the
 * browser reconnects on any drop. There is deliberately no manual retry loop
 * layered on top — two of them fight, and the native one already backs off when
 * the tab is hidden.
 *
 * A closed stream is not an error state worth surfacing: the floor plan keeps
 * whatever it last rendered and the next `onReady` re-syncs it from REST, which
 * is what makes a laptop lid closing over lunch a non-event.
 */
export function openLiveFeed(tenantId: string, handlers: LiveHandlers): () => void {
  if (typeof window === "undefined" || typeof window.EventSource === "undefined") {
    return () => {};
  }
  const base = apiBaseUrl();
  if (!base) return () => {};
  const url = `${base.replace(/\/+$/, "")}/web/live/${encodeURIComponent(tenantId)}`;

  let source: EventSource | null = null;
  try {
    source = new EventSource(url);
  } catch {
    return () => {};
  }

  const parse = <T,>(event: MessageEvent): T | null => {
    try {
      return JSON.parse(event.data) as T;
    } catch {
      // A malformed frame is dropped, exactly as the POS drops a malformed RTDB
      // child. One bad payload must not tear down a working stream.
      return null;
    }
  };

  const listeners: Array<[string, (event: MessageEvent) => void]> = [
    [
      "ready",
      () => handlers.onReady?.(),
    ],
    [
      LIVE_EVENT.ORDER_CREATED,
      (event) => {
        const order = parse<LiveOrder>(event);
        if (order?._id) handlers.onOrderCreated?.(order);
      },
    ],
    [
      LIVE_EVENT.ORDER_UPDATED,
      (event) => {
        const order = parse<LiveOrder>(event);
        if (order?._id) handlers.onOrderUpdated?.(order);
      },
    ],
    [
      LIVE_EVENT.ORDER_DELETED,
      (event) => {
        const payload = parse<{ _id?: string }>(event);
        if (payload?._id) handlers.onOrderDeleted?.(String(payload._id));
      },
    ],
    [
      LIVE_EVENT.TABLE_UPDATED,
      (event) => {
        const table = parse<LiveTable>(event);
        if (table?._id) handlers.onTableUpdated?.(table);
      },
    ],
  ];

  for (const [name, fn] of listeners) source.addEventListener(name, fn as EventListener);

  return () => {
    for (const [name, fn] of listeners) {
      source?.removeEventListener(name, fn as EventListener);
    }
    source?.close();
    source = null;
  };
}
